import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { Component, forwardRef } from "react";
import { NavLink, NavLinkProps } from "react-router-dom";
import ChevronDownIcon from "../resources/images/icons-chevron-down.svg";
import { Button } from "./Button";

// Simple ForwardRef wrapper for NavLink (used by MenuItem)
const NavLinkWithRef = forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => (
  <NavLink ref={ref} {...props} />
));
NavLinkWithRef.displayName = "NavLinkWithRef";

// ForwardRef wrapper for NavLink with custom active state logic for MUI Tab
interface TabNavLinkProps extends Omit<NavLinkProps, "className"> {
  className?: string;
  pageRender?: React.ComponentType<{ isActive: boolean; children: React.ReactNode }>;
  pageName?: string;
  isCustomActive?: boolean;
  isValueActive?: boolean;
}

const TabNavLink = forwardRef<HTMLAnchorElement, TabNavLinkProps>(
  (
    { pageRender: PageRender, pageName, isCustomActive, isValueActive, className, ...props },
    ref
  ) => {
    const activeClass = isCustomActive ? "active" : "";
    const combinedClassName = className ? `${className} ${activeClass}`.trim() : activeClass;

    if (PageRender && pageName) {
      return (
        <NavLink ref={ref} {...props} end className={combinedClassName}>
          <PageRender isActive={isValueActive || false}>{pageName}</PageRender>
        </NavLink>
      );
    }
    return <NavLink ref={ref} {...props} end className={combinedClassName} />;
  }
);
TabNavLink.displayName = "TabNavLink";

export class PillTabs extends Component<any, any> {
  static defaultProps = {
    padding: 23,
    title: null
  };

  constructor(props: any) {
    super(props);

    this.state = {
      showMenu: false,
      dropButton: null
    };
  }

  onDropDownClick(e: any) {
    const { theme } = this.props;
    if (!theme.isBreakpointDown("md")) return;
    this.setState({
      showMenu: true,
      dropButton: e.target.classList.contains("klayo-pilltabs_tabs_dropbutton")
        ? e.target.parentElement.parentElement
        : e.target
    });
  }

  onMenuClose(e: any) {
    this.setState({ showMenu: false });
  }

  renderItems(items: any, tabMode: any) {
    const { user, organization, path, basePath, params } = this.props;
    return items
      .filter(([_, page]: any) => !!page)
      .map(([key, page]: any) => {
        const value = basePath + (page.paths[0] !== "/" ? page.paths[0] : "");
        const to =
          page.paths[0] === "/"
            ? basePath + (params ? "?" + params : "")
            : basePath + page.paths[0] + (params ? "?" + params : "");
        // Custom active state logic (replaces v5 isActive prop)
        const isCustomActive =
          user && page.isDefault && page.isDefault(user)
            ? path === basePath || (page.allowRoot && path === page.paths[0])
            : path.startsWith(basePath + page.paths[0]);

        return tabMode ? (
          <Tab
            key={key}
            value={value}
            to={to}
            label={page.name}
            disableRipple={true}
            disabled={!user || !organization || !page.hasAccess(user, organization)}
            component={TabNavLink}
            pageRender={page.render}
            pageName={page.name}
            isCustomActive={isCustomActive}
            isValueActive={value === path}
            sx={{
              display: {
                xs: value === path ? "block" : "none",
                md: "block"
              },
              padding: {
                xs: "4px 12px",
                md: "12px 24px"
              },
              height: {
                xs: "32px",
                md: "32px"
              },
              minHeight: {
                xs: "32px",
                md: "48px"
              }
            }}
          />
        ) : (
          <MenuItem
            key={key}
            to={to}
            disabled={!user || !organization || !page.hasAccess(user, organization)}
            component={NavLinkWithRef}
            onClick={this.onMenuClose.bind(this)}
          >
            <ListItemText disableTypography={true}>{page.name}</ListItemText>
          </MenuItem>
        );
      });
  }

  render() {
    const { user, organization, theme, sx, className, path, pages, basePath } = this.props;
    const { dropButton, showMenu } = this.state;

    // Calculate valid tab values to check if path matches any available tab
    const validTabValues = Object.entries(pages)
      .filter(([_, page]: any) => !!page)
      .map(([_, page]: any) => basePath + (page.paths[0] !== "/" ? page.paths[0] : ""));

    // Use false if path doesn't match any tab (prevents MUI warning)
    const tabValue = validTabValues.includes(path) ? path : false;

    return (
      <Box
        className='klayo-pilltabs'
        sx={{
          height: {
            xs: "48px",
            md: "64px"
          },
          cursor: {
            xs: "pointer",
            sm: "pointer"
          },
          ...sx
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          onClick={this.onDropDownClick.bind(this)}
        >
          <Tabs
            className={"klayo-pilltabs_tabs " + className}
            value={tabValue}
            TabIndicatorProps={{
              sx: {
                display: { xs: "none", sm: "none", md: "block" },
                height: { xs: "0", sm: "0", md: "46px" }
              }
            }}
          >
            {this.renderItems(Object.entries(pages), true)}
          </Tabs>
          <Button
            className='klayo-pilltabs_tabs_dropbutton'
            icon={<ChevronDownIcon />}
            onClick={this.onDropDownClick.bind(this)}
            sx={{
              display: { xs: "block", sm: "block", md: "none" },
              padding: "6px 14px 0 0",
              margin: "0 4px 0 0"
            }}
          />
        </Stack>

        <Menu
          className='klayo-pilltabs_menu'
          open={showMenu}
          anchorEl={dropButton}
          onClose={this.onMenuClose.bind(this)}
          PaperProps={{
            sx: {
              width: { xs: "calc(100vw - 48px)", sm: "calc(100vw - 82px)" },
              maxWidth: "100%",
              left: { xs: "32px", sm: "48px" },
              right: { xs: "32px", sm: "48px" }
            }
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {this.renderItems(Object.entries(pages), false)}
        </Menu>
      </Box>
    );
  }
}
